import { useGetDailyShifts } from 'hook/query/useGetDailyShifts'
import { ShiftOptions } from 'models/ICattlesData'
import { useCallback, useEffect, useMemo, useState } from 'react'

export const useShiftFilters = (nullShifts: ShiftOptions[] = []) => {
	const [selectedShifts, setSelectedShifts] = useState<(string | number)[]>([])
	const { data: dailyShifts } = useGetDailyShifts({})

	const handleToggleShift = useCallback((id: string | number) => {
		setSelectedShifts(prev => {
			if (prev.includes(id)) {
				return prev.filter(item => item !== id)
			}
			return [...prev, id]
		})
	}, [])

	const handleRemoveShift = useCallback((id: string | number) => {
		setSelectedShifts(prev => prev.filter(item => item !== id))
	}, [])

	useEffect(() => {
		if (nullShifts.length === 0) return

		setSelectedShifts(prev =>
			prev.filter(item => !nullShifts.includes(item as ShiftOptions)),
		)
	}, [nullShifts])

	const availableShifts = useMemo(() => {
		if (!Array.isArray(dailyShifts) || dailyShifts.length === 0) return []
		return dailyShifts.map(shift => ({
			title: shift.title,
			key: shift.value,
		}))
	}, [dailyShifts])

	const shiftBreadcrumbs = useMemo(() => {
		return availableShifts.map(tab => ({
			id: tab.key,
			title: tab.title,
			selected: selectedShifts.includes(tab.key),
			disabled: nullShifts.includes(tab.key as ShiftOptions),
		}))
	}, [availableShifts, selectedShifts, nullShifts])

	const shift: ShiftOptions[] | 'all' = useMemo(() => {
		if (selectedShifts.length === 0) return 'all'
		const validShifts = selectedShifts.filter(
			shift =>
				typeof shift === 'string' &&
				Object.values(ShiftOptions).includes(shift as ShiftOptions),
		) as ShiftOptions[]
		return validShifts.length > 0 ? validShifts : 'all'
	}, [selectedShifts])

	return {
		selectedShifts,
		handleToggleShift,
		handleRemoveShift,
		availableShifts,
		shiftBreadcrumbs,
		shift,
	}
}
